import type { TopCategoryViewModel } from 'api/types/top-category';
import type { ProductTypeViewModel } from 'api/types/product-type';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ListingFilterState } from 'types/listingFilterState';
import Button from 'components/Button';
import { iconMap } from 'utils/constants/iconMap';
import './Filter.css';

export interface SlidingCategoryNavigatorProps {
  topCategories: TopCategoryViewModel[];
  secondaryCategories: { id: number; name?: string | null }[];
  productTypes: ProductTypeViewModel[];
  draft: ListingFilterState;
  setDraftCategory: (level: 'top' | 'secondary' | 'productType', id: number | null) => void;
}

type PanelDepth = 0 | 1 | 2;

/**
 * Multi-level category picker with horizontal slide transitions (CSS transform).
 * Auto-skip for single-child levels is handled by setDraftCategory in useListingSearch; depth syncs to draft.
 */
export default function SlidingCategoryNavigator({
  topCategories,
  secondaryCategories,
  productTypes,
  draft,
  setDraftCategory,
}: SlidingCategoryNavigatorProps) {
  const [depth, setDepth] = useState<PanelDepth>(0);
  const [skippedSecondary, setSkippedSecondary] = useState(false);
  const [viewportHeight, setViewportHeight] = useState<number | undefined>(undefined);
  const panel0Ref = useRef<HTMLDivElement>(null);
  const panel1Ref = useRef<HTMLDivElement>(null);
  const panel2Ref = useRef<HTMLDivElement>(null);

  const syncDepthFromDraft = useCallback(() => {
    if (draft.topCategoryId == null) {
      setDepth(0);
      setSkippedSecondary(false);
      return;
    }
    const top = topCategories.find((t) => t.id === draft.topCategoryId);
    const secs = top?.secondaryCategories ?? [];
    if (secs.length <= 1) {
      setSkippedSecondary(secs.length === 1);
      setDepth(2);
      return;
    }
    setSkippedSecondary(false);
    if (draft.secondaryCategoryId == null) setDepth(1);
    else setDepth(2);
  }, [draft.topCategoryId, draft.secondaryCategoryId, topCategories]);

  useEffect(() => {
    syncDepthFromDraft();
  }, [syncDepthFromDraft]);

  const activePanelIndex: 0 | 1 | 2 = depth === 0 ? 0 : depth === 1 ? 1 : 2;

  const measureActivePanel = useCallback(() => {
    const el =
      activePanelIndex === 0 ? panel0Ref.current : activePanelIndex === 1 ? panel1Ref.current : panel2Ref.current;
    if (!el) return;
    setViewportHeight(el.getBoundingClientRect().height);
  }, [activePanelIndex]);

  useLayoutEffect(() => {
    measureActivePanel();
  }, [
    measureActivePanel,
    depth,
    topCategories,
    secondaryCategories,
    productTypes,
    draft.topCategoryId,
    draft.secondaryCategoryId,
    draft.productTypeId,
  ]);

  useEffect(() => {
    const el =
      activePanelIndex === 0 ? panel0Ref.current : activePanelIndex === 1 ? panel1Ref.current : panel2Ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measureActivePanel());
    ro.observe(el);
    return () => ro.disconnect();
  }, [activePanelIndex, measureActivePanel, topCategories, secondaryCategories, productTypes]);

  const handleBack = () => {
    if (depth === 0) return;
    if (depth === 2 && skippedSecondary) {
      setDraftCategory('top', null);
      return;
    }
    if (depth === 2 && !skippedSecondary) {
      setDraftCategory('productType', null);
      setDepth(1);
      return;
    }
    if (depth === 1) {
      setDraftCategory('top', null);
    }
  };

  const handleSelectTop = (id: number | null) => {
    setDraftCategory('top', id);
  };

  const handleSelectSecondary = (id: number | null) => {
    setDraftCategory('secondary', id);
    setDepth(2);
  };

  const handleSelectProductType = (id: number | null) => {
    setDraftCategory('productType', id);
  };

  const translatePct = depth === 0 ? 0 : depth === 1 ? -33.333 : -66.666;

  const itemClass = (active: boolean) => `sb-category-item${active ? ' sb-category-item--active' : ''}`;

  return (
    <div className="sliding-category-nav">
      {depth > 0 && (
        <Button
          type="button"
          className="sb-category-back"
          onClick={handleBack}
        >
          <i className="fas fa-arrow-left" aria-hidden />
          Geri
        </Button>
      )}
      <div
        className="sliding-category-viewport overflow-hidden"
        style={viewportHeight != null ? { height: viewportHeight, transition: 'height 0.2s ease-out' } : undefined}
      >
        <div
          className="sliding-category-track d-flex align-items-start"
          style={{
            width: '300%',
            transform: `translateX(${translatePct}%)`,
            transition: 'transform 0.28s ease-out',
          }}
        >
          <div
            ref={panel0Ref}
            className="sliding-category-panel"
            style={{ width: '33.333%', flexShrink: 0 }}
          >
            <div className="sb-category-list">
              {topCategories.map((top) => (
                <Button
                  key={top.id}
                  type="button"
                  className={itemClass(draft.topCategoryId === top.id)}
                  onClick={() => handleSelectTop(top.id)}
                >
                  <span className="sb-category-item__label">{top.name ?? `Kategori ${top.id}`}</span>
                  <i className="fas fa-chevron-right sb-category-item__chevron" aria-hidden />
                </Button>
              ))}
            </div>
          </div>
          <div
            ref={panel1Ref}
            className="sliding-category-panel"
            style={{ width: '33.333%', flexShrink: 0 }}
          >
            <div className="sb-category-list">
              {secondaryCategories.map((sec) => (
                <Button
                  key={sec.id}
                  type="button"
                  className={itemClass(draft.secondaryCategoryId === sec.id)}
                  onClick={() => handleSelectSecondary(sec.id)}
                >
                  <span className="sb-category-item__label">{sec.name ?? `Alt ${sec.id}`}</span>
                  <i className="fas fa-chevron-right sb-category-item__chevron" aria-hidden />
                </Button>
              ))}
              {secondaryCategories.length === 0 && draft.topCategoryId != null && (
                <div className="text-muted small p-2">Bu kategoride alt seçenek yok.</div>
              )}
            </div>
          </div>
          <div
            ref={panel2Ref}
            className="sliding-category-panel"
            style={{ width: '33.333%', flexShrink: 0 }}
          >
            <div className="sb-category-list">
              {productTypes.map((pt) => (
                <Button
                  key={pt.id}
                  type="button"
                  className={itemClass(draft.productTypeId === pt.id)}
                  onClick={() => handleSelectProductType(pt.id)}
                >
                  <span className="sb-category-item__label">
                    <i className={`${iconMap[pt.id] ?? 'fas fa-microchip'} sb-category-item__icon`} aria-hidden />
                    {pt.name ?? `Ürün ${pt.id}`}
                  </span>
                </Button>
              ))}
              {productTypes.length === 0 && draft.secondaryCategoryId != null && (
                <div className="text-muted small p-2">Ürün tipi bulunamadı.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
